import React, { useState, useEffect, useRef } from "react";
import { Calendar, ChevronDown, ChevronLeft, ChevronRight, X } from "lucide-react";

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
    return new Date(year, month, 1).getDay();
}

function stripTime(d) {
    if (!d) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function isSameDay(a, b) {
    if (!a || !b) return false;
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
}

function isInRange(date, start, end) {
    if (!start || !end || !date) return false;
    const d = stripTime(date);
    let s = stripTime(start);
    let e = stripTime(end);
    if (s > e) {
        const tmp = s;
        s = e;
        e = tmp;
    }
    return d >= s && d <= e;
}

function CalendarMonth({ year, month, startDate, endDate, hoverDate, onDayClick, onDayHover }) {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);

    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

    const displayMonthName = MONTHS[month].substring(0, 3);
    const effectiveEnd = endDate || hoverDate;

    return (
        <div className="w-48 select-none">
            {/* Month label */}
            <p className="text-center text-[12px] font-semibold text-[#151D48] mb-1.5">
                {displayMonthName} {year}
            </p>
            {/* Day headers */}
            <div className="grid grid-cols-7 mb-1">
                {DAYS.map((d) => (
                    <div key={d} className="text-center text-[10px] font-medium text-[#9CA3AF] py-0.5">
                        {d}
                    </div>
                ))}
            </div>
            {/* Day cells */}
            <div className="grid grid-cols-7">
                {cells.map((date, idx) => {
                    if (!date) return <div key={`empty-${idx}`} className="h-7" />;

                    const isStart = isSameDay(date, startDate);
                    const isEnd = isSameDay(date, endDate);
                    const inRange = isInRange(date, startDate, effectiveEnd);
                    const isSelected = isStart || isEnd;

                    return (
                        <div
                            key={idx}
                            className="relative flex items-center justify-center h-7"
                            onClick={() => onDayClick(date)}
                            onMouseEnter={() => onDayHover(date)}
                        >
                            {/* Range ribbon behind circle */}
                            {inRange && startDate && effectiveEnd && !isSameDay(startDate, effectiveEnd) && (
                                <div
                                    className={`absolute inset-y-1 bg-[#EEF0FF] ${isStart ? "left-1/2 right-0" :
                                        isSameDay(date, effectiveEnd) ? "left-0 right-1/2" :
                                            "left-0 right-0"
                                        }`}
                                />
                            )}

                            {/* Circle */}
                            <span
                                className={`relative z-10 flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-medium cursor-pointer transition-all
                                    ${isSelected ? "bg-[#e8423a] text-white shadow font-bold" : ""}
                                    ${inRange && !isSelected ? "text-[#e8423a] font-semibold" : ""}
                                    ${!isSelected && !inRange ? "hover:bg-[#E8E9FF] text-[#374151]" : ""}
                                `}
                            >
                                {date.getDate()}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

const DateRangePicker = ({ value, onChange }) => {
    const [startDate, setStartDate] = useState(value?.[0] || null);
    const [endDate, setEndDate] = useState(value?.[1] || null);
    const [isOpen, setIsOpen] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [hoverDate, setHoverDate] = useState(null);
    const containerRef = useRef(null);

    const today = new Date();
    const [viewYear, setViewYear] = useState(today.getFullYear());
    const [viewMonth, setViewMonth] = useState(today.getMonth());

    // Compare timestamps to safely update internal state ONLY when external value prop changes
    const propStartTs = value?.[0] ? stripTime(value[0]) : null;
    const propEndTs = value?.[1] ? stripTime(value[1]) : null;
    const prevPropsRef = useRef({ start: propStartTs, end: propEndTs });

    useEffect(() => {
        if (prevPropsRef.current.start !== propStartTs || prevPropsRef.current.end !== propEndTs) {
            prevPropsRef.current = { start: propStartTs, end: propEndTs };
            setStartDate(value?.[0] || null);
            setEndDate(value?.[1] || null);
        }
    }, [propStartTs, propEndTs, value]);

    const handlePrev = () => {
        if (viewMonth === 0) {
            setViewMonth(11);
            setViewYear((y) => y - 1);
        } else {
            setViewMonth((m) => m - 1);
        }
    };

    const handleNext = () => {
        if (viewMonth === 11) {
            setViewMonth(0);
            setViewYear((y) => y + 1);
        } else {
            setViewMonth((m) => m + 1);
        }
    };

    const getPresetRanges = () => {
        const now = new Date();
        const yesterday = new Date();
        yesterday.setDate(now.getDate() - 1);

        const last7 = new Date();
        last7.setDate(now.getDate() - 6);

        const last30 = new Date();
        last30.setDate(now.getDate() - 29);

        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

        return {
            "All Time": [null, null],
            Today: [now, now],
            Yesterday: [yesterday, yesterday],
            "Last 7 Days": [last7, now],
            "Last 30 Days": [last30, now],
            "This Month": [thisMonthStart, now],
            "Last Month": [lastMonthStart, lastMonthEnd]
        };
    };

    const getActivePreset = () => {
        if (!startDate && !endDate) return "All Time";
        if (!startDate || !endDate) return "Custom Range";
        const ranges = getPresetRanges();
        for (const [key, val] of Object.entries(ranges)) {
            if (key === "All Time") continue;
            if (isSameDay(startDate, val[0]) && isSameDay(endDate, val[1])) {
                return key;
            }
        }
        return "Custom Range";
    };

    const handlePresetClick = (label) => {
        if (label === "All Time") {
            setStartDate(null);
            setEndDate(null);
            setShowCalendar(false);
            if (onChange) onChange(null, null);
            setIsOpen(false);
            return;
        }

        const ranges = getPresetRanges();
        const [start, end] = ranges[label];
        setStartDate(start);
        setEndDate(end);
        setShowCalendar(false);
        if (onChange) onChange(start, end);
        setIsOpen(false);
    };

    const handleDayClick = (date) => {
        if (!startDate || (startDate && endDate)) {
            // First click: set start date, reset end date
            setStartDate(date);
            setEndDate(null);
            setHoverDate(null);
        } else {
            // Second click: set end date, order them correctly
            let finalStart = startDate;
            let finalEnd = date;

            if (stripTime(date) < stripTime(startDate)) {
                finalStart = date;
                finalEnd = startDate;
            }

            setStartDate(finalStart);
            setEndDate(finalEnd);
            setHoverDate(null);
            if (onChange) onChange(finalStart, finalEnd);
            setIsOpen(false);
        }
    };

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const formatDateString = (date) => {
        if (!date) return "";
        return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric"
        });
    };

    const activePresetLabel = getActivePreset();

    const handleTriggerClick = (e) => {
        if (e.target.closest(".clear-btn")) return;

        if (!isOpen) {
            // Sync with parent props when opening
            setStartDate(value?.[0] || null);
            setEndDate(value?.[1] || null);

            const activePreset = getActivePreset();
            setShowCalendar(activePreset === "Custom Range");
            const targetDate = value?.[0] || today;
            setViewYear(targetDate.getFullYear());
            setViewMonth(targetDate.getMonth());
        }
        setIsOpen(!isOpen);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setStartDate(null);
        setEndDate(null);
        setShowCalendar(false);
        if (onChange) onChange(null, null);
    };

    const rightMonth = viewMonth === 11 ? 0 : viewMonth + 1;
    const rightYear = viewMonth === 11 ? viewYear + 1 : viewYear;

    return (
        <div ref={containerRef} className="relative w-full">
            {/* Trigger input */}
            <div
                onClick={handleTriggerClick}
                className="flex cursor-pointer items-center justify-between gap-2 w-full appearance-none rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-[13px] font-medium text-[#151D48] outline-none transition-all hover:border-[#e8423a] select-none shadow-xs"
            >
                <div className="flex items-center gap-2 truncate">
                    <Calendar size={14} className="text-gray-500 shrink-0" />
                    <span className="text-gray-700 font-medium text-[13px] truncate">
                        {startDate && endDate
                            ? `${formatDateString(startDate)} - ${formatDateString(endDate)}`
                            : startDate
                                ? `${formatDateString(startDate)} - Pick End Date`
                                : "All Dates"}
                    </span>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                    {(startDate || endDate) && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="clear-btn p-0.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Clear date filter"
                        >
                            <X size={12} />
                        </button>
                    )}
                    <ChevronDown size={13} className="text-gray-500" />
                </div>
            </div>

            {/* Dropdown panel */}
            {isOpen && (
                <div className="absolute right-0 top-[calc(100%+6px)] z-50 bg-white border border-gray-200 rounded-xl shadow-xl flex flex-col md:flex-row overflow-hidden min-w-auto">
                    {/* Left Pane: Presets */}
                    <div className="w-full md:w-36 border-b md:border-b-0 md:border-r border-gray-100 py-1.5 flex flex-col bg-gray-50/70 select-none shrink-0">
                        {["All Time", "Today", "Yesterday", "Last 7 Days", "Last 30 Days", "This Month", "Last Month"].map((label) => {
                            const isActive = activePresetLabel === label && !showCalendar;
                            return (
                                <div
                                    key={label}
                                    onClick={() => handlePresetClick(label)}
                                    className={`px-3.5 py-2 hover:bg-gray-100 cursor-pointer text-[12px] font-medium transition-all
                                        ${isActive ? "bg-[#e8423a] text-white hover:bg-[#e8423a]" : "text-gray-600"}
                                    `}
                                >
                                    {label}
                                </div>
                            );
                        })}
                        <div
                            onClick={() => setShowCalendar(true)}
                            className={`px-3.5 py-2 hover:bg-gray-100 cursor-pointer text-[12px] font-medium transition-all border-t border-gray-200/50 mt-1
                                ${showCalendar ? "bg-[#e8423a] text-white hover:bg-[#e8423a]" : "text-gray-700 font-semibold"}
                            `}
                        >
                            Custom Range
                        </div>
                    </div>

                    {/* Right Pane: Dual Calendar */}
                    {showCalendar && (
                        <div className="p-3.5 flex flex-col border-t md:border-t-0 md:border-l border-gray-100 bg-white">
                            {/* Nav header */}
                            <div className="flex items-center justify-between mb-2.5 px-1">
                                <button
                                    onClick={handlePrev}
                                    className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 transition-all cursor-pointer"
                                    type="button"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                                    Select Date Range
                                </span>
                                <button
                                    onClick={handleNext}
                                    className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 transition-all cursor-pointer"
                                    type="button"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>

                            {/* Calendars side-by-side or stacked on mobile */}
                            <div className="flex flex-col sm:flex-row gap-4">
                                <CalendarMonth
                                    year={viewYear}
                                    month={viewMonth}
                                    startDate={startDate}
                                    endDate={endDate}
                                    hoverDate={hoverDate}
                                    onDayClick={handleDayClick}
                                    onDayHover={setHoverDate}
                                />
                                <div className="hidden sm:block w-px bg-gray-100" />
                                <div className="hidden sm:block">
                                    <CalendarMonth
                                        year={rightYear}
                                        month={rightMonth}
                                        startDate={startDate}
                                        endDate={endDate}
                                        hoverDate={hoverDate}
                                        onDayClick={handleDayClick}
                                        onDayHover={setHoverDate}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DateRangePicker;

